import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { CreditCard, Truck, Eye, EyeOff, Tag, X, Loader2, Check } from 'lucide-react';
import { Badge } from '../ui/badge';

export default function CheckoutModal({ hub }) {
  const { showCheckoutModal, setShowCheckoutModal, checkoutForm, setCheckoutForm, checkoutLoading, handleCheckout, hasDropoffItems, quote, paymentType, promoCode, setPromoCode, promoResult, promoValidating, validatePromo, clearPromo } = hub;

  // Don't render if modal is not open or quote is not ready
  if (!showCheckoutModal || !quote || quote.subtotal === undefined) return null;

  return (
    <Dialog open={showCheckoutModal} onOpenChange={setShowCheckoutModal}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CreditCard className="h-5 w-5 text-indigo-500" /> Complete Your Booking</DialogTitle>
          <DialogDescription>{hasDropoffItems ? "Enter your details and drop-off address to complete your booking." : "Enter your details to complete your booking."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Contact */}
          <div className="space-y-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Contact Information</h4>
            <div className="space-y-2"><Label htmlFor="checkout-name">Your Name *</Label><Input id="checkout-name" value={checkoutForm.name} onChange={(e) => setCheckoutForm({ ...checkoutForm, name: e.target.value })} placeholder="John Doe" data-testid="checkout-name" /></div>
            <div className="space-y-2"><Label htmlFor="checkout-email">Email Address *</Label><Input id="checkout-email" type="email" value={checkoutForm.email} onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })} placeholder="john@example.com" data-testid="checkout-email" /></div>
            <div className="space-y-2"><Label htmlFor="checkout-phone">Phone (Optional)</Label><Input id="checkout-phone" type="tel" value={checkoutForm.phone} onChange={(e) => setCheckoutForm({ ...checkoutForm, phone: e.target.value })} placeholder="(555) 123-4567" data-testid="checkout-phone" /></div>
          </div>

          {/* Drop-off Address */}
          {hasDropoffItems && (
            <div className="space-y-4 border-t pt-4">
              <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2"><Truck className="h-4 w-4 text-emerald-500" /> Drop-off Address</h4>
              <p className="text-xs text-muted-foreground">Where should we deliver your drop-off items?</p>
              <div className="space-y-2"><Label>Street Address *</Label><Input value={checkoutForm.dropoffStreet} onChange={(e) => setCheckoutForm({ ...checkoutForm, dropoffStreet: e.target.value })} placeholder="123 Main St" data-testid="checkout-dropoff-street" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2"><Label>City *</Label><Input value={checkoutForm.dropoffCity} onChange={(e) => setCheckoutForm({ ...checkoutForm, dropoffCity: e.target.value })} placeholder="Denver" data-testid="checkout-dropoff-city" /></div>
                <div className="space-y-2"><Label>State *</Label><Input value={checkoutForm.dropoffState} onChange={(e) => setCheckoutForm({ ...checkoutForm, dropoffState: e.target.value })} placeholder="CO" maxLength={2} data-testid="checkout-dropoff-state" /></div>
                <div className="space-y-2"><Label>ZIP *</Label><Input value={checkoutForm.dropoffZip} onChange={(e) => setCheckoutForm({ ...checkoutForm, dropoffZip: e.target.value })} placeholder="80202" data-testid="checkout-dropoff-zip" /></div>
              </div>
              <div className="space-y-2"><Label>Delivery Instructions</Label><Textarea value={checkoutForm.dropoffInstructions} onChange={(e) => setCheckoutForm({ ...checkoutForm, dropoffInstructions: e.target.value })} placeholder="Gate code, loading dock info, etc." rows={2} data-testid="checkout-dropoff-instructions" /></div>
            </div>
          )}

          {/* Create Account */}
          <div className="border-t pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <Checkbox checked={checkoutForm.createAccount} onCheckedChange={(checked) => setCheckoutForm({ ...checkoutForm, createAccount: checked })} />
              <div><span className="font-medium">Create an account</span><p className="text-sm text-muted-foreground">Track your booking and manage payments</p></div>
            </label>
            {checkoutForm.createAccount && (
              <div className="mt-4 space-y-2">
                <Label htmlFor="checkout-password">Create Password *</Label>
                <Input id="checkout-password" type="password" value={checkoutForm.password} onChange={(e) => setCheckoutForm({ ...checkoutForm, password: e.target.value })} placeholder="Enter password" data-testid="checkout-password" />
              </div>
            )}
          </div>

          {/* Promo Code */}
          <div className="border-t pt-4">
            <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2 mb-2"><Tag className="h-4 w-4 text-amber-500" /> Promo Code</h4>
            <div className="flex gap-2">
              <Input value={promoCode} onChange={e => setPromoCode(e.target.value.toUpperCase())} placeholder="Enter code" className="font-mono text-sm" disabled={promoResult?.valid} data-testid="promo-input" />
              {promoResult?.valid ? (
                <Button variant="outline" onClick={clearPromo} size="sm" className="flex-shrink-0"><X className="h-4 w-4" /></Button>
              ) : (
                <Button onClick={validatePromo} disabled={promoValidating || !promoCode.trim()} size="sm" className="flex-shrink-0" data-testid="apply-promo">
                  {promoValidating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
                </Button>
              )}
            </div>
            {promoResult?.valid && (
              <div className="mt-2 flex items-center gap-2">
                <Badge className="bg-emerald-500"><Check className="h-3 w-3 mr-1" /> {promoResult.label}</Badge>
                <span className="text-xs text-emerald-600">-${promoResult.discount.toFixed(2)}</span>
              </div>
            )}
            {promoResult && !promoResult.valid && (
              <p className="text-xs text-red-500 mt-1">{promoResult.message}</p>
            )}
          </div>

          {/* Order Summary */}
          <div className="border-t pt-4 space-y-2">
            <h4 className="font-semibold text-sm">Order Summary</h4>
            <div className="flex justify-between text-sm"><span>Subtotal</span><span>${(quote.subtotal || 0).toFixed(2)}</span></div>
            {(quote.admin_service_fee || 0) > 0 && (
              <div className="flex justify-between text-sm">
                <span>Service Fee <span className="text-xs text-muted-foreground">(F&B)</span></span>
                <span>${(quote.admin_service_fee || 0).toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm"><span>Tax</span><span>${(quote.tax || quote.tax_amount || 0).toFixed(2)}</span></div>
            {promoResult?.valid && (
              <div className="flex justify-between text-sm text-emerald-600"><span>Promo ({promoResult.label})</span><span>-${promoResult.discount.toFixed(2)}</span></div>
            )}
            <div className="flex justify-between font-bold text-lg border-t pt-2"><span>Total</span><span>${((quote.total || 0) - (promoResult?.valid ? promoResult.discount : 0)).toFixed(2)}</span></div>
            {paymentType === 'plan' && (
              <div className="flex justify-between text-indigo-600 dark:text-indigo-400 font-semibold"><span>Due Today (50%)</span><span>${(quote.deposit_amount || 0).toFixed(2)}</span></div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowCheckoutModal(false)}>Cancel</Button>
          <Button onClick={handleCheckout} disabled={checkoutLoading} data-testid="confirm-checkout-btn">
            {checkoutLoading ? 'Processing...' : <><CreditCard className="mr-2 h-4 w-4" /> {paymentType === 'full' ? `Pay $${(quote.total || 0).toFixed(2)}` : `Pay Deposit $${(quote.deposit_amount || 0).toFixed(2)}`}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
